import { Parser } from 'n3';
import { ValidationError } from '../../../types/ontology';

export async function validateRDF(turtle: string): Promise<ValidationError[]> {
    return new Promise((resolve) => {
        const errors: ValidationError[] = [];
        const parser = new Parser();
        const lines = turtle.split('\n');

        try {
            parser.parse(turtle, (error: any, _quad: any, _prefixes: any) => {
                if (error) {
                    // N3 parser error - try to extract line/column info
                    const errorMessage = error.message || 'Parse error';

                    // N3 errors often include line info in format "line X"
                    const lineMatch = errorMessage.match(/line (\d+)/i);
                    const line = lineMatch ? parseInt(lineMatch[1], 10) : 1;

                    errors.push({
                        line,
                        column: 1,
                        message: errorMessage,
                        severity: 'error'
                    });
                }
            });
        } catch (e) {
            // Synchronous parse error
            const errorMessage = e instanceof Error ? e.message : 'Unknown parse error';
            errors.push({
                line: 1,
                column: 1,
                message: errorMessage,
                severity: 'error'
            });
        }

        // Additional linting checks
        const warnings = lintTurtle(lines);
        errors.push(...warnings);

        // Sort by line number
        errors.sort((a, b) => a.line - b.line);

        resolve(errors);
    });
}

function lintTurtle(lines: string[]): ValidationError[] {
    const warnings: ValidationError[] = [];
    let inMultilineString = false;

    lines.forEach((line, index) => {
        const lineNum = index + 1;
        const trimmed = line.trim();

        // Skip empty lines and comments
        if (!trimmed || trimmed.startsWith('#')) return;

        // Track multiline strings
        const tripleQuotes = (line.match(/"""/g) || []).length;
        if (tripleQuotes % 2 !== 0) {
            inMultilineString = !inMultilineString;
        }
        if (inMultilineString) return;

        // Check for common issues

        // 1. Missing label on class/property definitions
        if (trimmed.includes('owl:Class') || trimmed.includes('owl:ObjectProperty') || trimmed.includes('owl:DatatypeProperty')) {
            // This is a simple heuristic - could be improved
            // We'd need to look ahead for rdfs:label
        }

        // 2. Unusual prefix patterns
        if (trimmed.startsWith('@prefix')) {
            if (!trimmed.endsWith('.')) {
                warnings.push({
                    line: lineNum,
                    column: 1,
                    message: 'Prefix declaration should end with a period',
                    severity: 'warning'
                });
            }
        }

        // 3. Check for base URI if relative URIs are used
        if (/<[^>]+>/.test(line) && line.includes('<>')) {
            warnings.push({
                line: lineNum,
                column: 1,
                message: 'Empty URI found - consider using a base URI',
                severity: 'info'
            });
        }

        // 4. Very long lines
        if (line.length > 200) {
            warnings.push({
                line: lineNum,
                column: 200,
                message: 'Line is very long - consider breaking it up',
                severity: 'info'
            });
        }
    });

    return warnings;
}

// OWL-specific validation
export async function validateOWL(_turtle: string): Promise<ValidationError[]> {
    const errors: ValidationError[] = [];

    // TODO: Implement structural OWL validation
    // - Check domain/range consistency
    // - Check class hierarchy cycles
    // - Validate cardinality constraints

    return errors;
}
